import React, {createContext, useCallback, useContext, useMemo} from 'react';

import type {DisabledFeaturesMap} from './types';

type RunSelectorFeaturesContextType = {
  enableFreezeRunset?: boolean;
  enableSetProject?: boolean;
  disableRunLinks?: boolean;
  // maybe:
  // displayBenchmark?: boolean;
  // isExpandable?: boolean;
  // showArtifactCounts?: boolean;
  // showLogCounts?: boolean;
  // others???
} & DisabledFeaturesMap;

type Feature =
  | 'freezeRunset'
  | 'setProject'
  | 'runLinks'
  | keyof DisabledFeaturesMap;

function featureIsActive(
  ctx: RunSelectorFeaturesContextType,
  feature: Feature
): boolean {
  // Special case for 'features' that are described positively rather than
  // negatively. So 'enableThing' vs 'disableThing':
  if (feature === 'freezeRunset') {
    return Boolean(ctx.enableFreezeRunset);
  }
  if (feature === 'setProject') {
    return Boolean(ctx.enableSetProject);
  }
  if (feature === 'runLinks') {
    return !ctx.disableRunLinks;
  }
  // The remaining features are disabled-if-true:
  return !ctx[feature];
}

const RunSelectorFeaturesContext =
  createContext<RunSelectorFeaturesContextType>({});

export function useRunSelectorFeatureEnabled() {
  const ctx = useContext(RunSelectorFeaturesContext);
  return useCallback(
    (feature: Feature) => featureIsActive(ctx, feature),
    [ctx]
  );
}

export function useRunSelectorFeatureDisabled() {
  const ctx = useContext(RunSelectorFeaturesContext);
  return useCallback(
    (feature: Feature) => !featureIsActive(ctx, feature),
    [ctx]
  );
}

type RunSelectorFeaturesProviderProps = {
  disabledFeatures?: DisabledFeaturesMap;
  enableFreezeRunset?: boolean;
  enableSetProject?: boolean;
  disableRunLinks?: boolean;
  children?: React.ReactNode;
};

/**
 * Acts as a shim over DisabledFeaturesMap and a few other boolean flags that
 * are passed to the run selector. This context provides a consistent way for
 * children to check if a particular feature is enabled or not. The only way
 * to read this context is through the `useRunSelectorFeatureEnabled` and
 * `useRunSelectorFeatureDisabled` hooks, which obscure the storage format of
 * run selector features.
 *
 * @param disabledFeatures
 * @param enableFreezeRunset
 * @param enableSetProject
 * @param disableRunLinks
 * @param children
 * @constructor
 */
export function RunSelectorFeaturesProvider({
  disabledFeatures = {},
  enableFreezeRunset,
  enableSetProject,
  disableRunLinks,
  children,
}: RunSelectorFeaturesProviderProps) {
  const value = useMemo(
    () => ({
      ...disabledFeatures,
      enableFreezeRunset,
      enableSetProject,
      disableRunLinks,
    }),
    [disableRunLinks, disabledFeatures, enableFreezeRunset, enableSetProject]
  );

  return (
    <RunSelectorFeaturesContext.Provider value={value}>
      {children}
    </RunSelectorFeaturesContext.Provider>
  );
}
